import { createAsyncThunk } from "@reduxjs/toolkit";

import { loadDeploymentNextRequest } from "Api/organizations/projects/environments/deployments/loadDeploymentNext.request";
import logger from "Libs/logger";
import { getProjectId } from "Libs/utils";
import { getCommonError } from "Reducers/sliceFactory";

import {
  deploymentSizingApiEnabledSelector,
  isNextDeploymentFetchedSelector,
  loadingNextDeploymentSelector
} from "../deployment.selectors";

import type {
  LoadDeploymentNextProps,
  LoadDeploymentNextPayload
} from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadDeploymentNext = createAsyncThunk<
  LoadDeploymentNextPayload,
  LoadDeploymentNextProps,
  AsyncThunkOptionType
>(
  "app/environmentDeployment/next",
  async (
    { organizationId, projectId, environmentId, settings = {} },
    { getState, rejectWithValue }
  ) => {
    try {
      const encodedEnvId = encodeURIComponent(environmentId);

      const projectDescriptionId = getProjectId(getState, projectId)!;
      const deployment = await loadDeploymentNextRequest({
        projectId: projectDescriptionId,
        environmentId: encodedEnvId,
        verbose: true,
        // See loadDeploymentCurrent.thunk.ts (CON-1655).
        fresh: settings.reload
      });

      const schemaProperties =
        deployment?._links?.self?.meta?.get?.responses?.default?.content?.[
          "application/json"
        ]?.schema?.properties;

      return {
        deployment,
        schemas: schemaProperties
      };
    } catch (error) {
      if (![404, 403].includes((error as { code: number }).code)) {
        const commonError = getCommonError(error).error;
        const errorMessage =
          commonError?.message ??
          "An error occurred while attempting to load deployment.";

        logger(errorMessage, {
          action: "load_deployment_next",
          meta: {
            organizationDescriptionId: organizationId,
            environmentDescriptionId: environmentId,
            projectDescriptionId: projectId
          }
        });
      }

      return rejectWithValue(error as { code: number; message: string });
    }
  },
  {
    condition: (
      { organizationId, projectId, environmentId, settings = {} },
      { getState }
    ) => {
      const { reload } = settings;
      const state = getState();
      const isSizingApiEnabled = deploymentSizingApiEnabledSelector(state, {
        organizationId,
        projectId,
        environmentId
      });

      const hasNecessaryProps = !!(
        organizationId &&
        projectId &&
        environmentId
      );
      const loadingNextDeployment = loadingNextDeploymentSelector(state);

      /*
       * The deployments/next endpoint needs to be dispatched even when "environment.has_deployment" is false since there is need for this information on
       * the Configure Resources page if a user chooses the manual strategy and
       * the project/environment page for the ResourcesSticker component (apps/console/src/common/components/EnvironmentTree/ResourcesSticker/ResourcesSticker.tsx)
       */
      if (!isSizingApiEnabled || !hasNecessaryProps) {
        return false;
      }

      /*
       * `loadingNext` is global, not per-environment. `reload` overrides it so
       * an explicit refresh is not silently dropped by an unrelated
       * environment's in-flight fetch; see loadDeploymentCurrent.thunk.ts.
       */
      if (!reload && loadingNextDeployment) {
        return false;
      }

      // Ask once per environment; see loadDeploymentCurrent.thunk.ts (CON-1655).
      if (
        !reload &&
        isNextDeploymentFetchedSelector(state, {
          organizationId,
          projectId,
          environmentId
        })
      ) {
        return false;
      }
    }
  }
);
